/**
 * @fileoverview A hook that loads the fonts for the application.
 *
 * @example
 * const { result } = renderHook(() => useFonts())
 * expect(result.current).toBe(true) // When fonts are loaded
 */

import { useEffect, useState } from 'react'
import config from '../config'

/**
 * @function getStaticURL
 * @description Get the static URL for a file.
 * @param {string} file - The file name.
 * @returns {string} - The static URL.
 */
function getStaticURL(file: string) {
    return config.s3.buckets.static + '/' + file
}

/**
 * @function useFonts
 * @description Hook to load the fonts for the application.
 * @returns {boolean} - Whether the fonts are loaded.
 */
export function useFonts(): boolean {
    const [areFontsLoaded, setAreFontsLoaded] = useState<boolean>(false)

    useEffect(() => {
        const fonts: FontFace[] = [
            new FontFace(
                'Gelica',
                `url(${getStaticURL('08b919ee-494b-4a67-a796-d7932290f8de.woff2')})`
            ),
            new FontFace('Inter', `url(${getStaticURL('Inter+Web/Inter-Light.woff2')})`, {
                weight: '400',
            }),
            new FontFace('Inter', `url(${getStaticURL('Inter+Web/Inter-Medium.woff2')})`, {
                weight: '500',
            }),
            new FontFace('Inter', `url(${getStaticURL('Inter+Web/Inter-SemiBold.woff2')})`, {
                weight: '600',
            }),
            new FontFace('Inter', `url(${getStaticURL('Inter+Web/Inter-Bold.woff2')})`, {
                weight: '700',
            }),
        ]

        Promise.all(fonts.map((font) => font.load()))
            .then((fonts) => {
                fonts.forEach((font) => document.fonts.add(font))
            })
            .finally(() => {
                setAreFontsLoaded(true)
            })
    }, [])

    return areFontsLoaded
}
